import { Card, Descriptions, Tag, Space, Typography, Empty, Image } from '@/shared/ui/primitives';
import { useTranslation } from 'react-i18next';
import {
  LinkOutlined, WhatsAppOutlined, UserOutlined, HomeOutlined,
  CoffeeOutlined, ManOutlined, WomanOutlined,
} from '@/shared/ui/icons';

type SourceMetadata = {
  source?: string;
  sourceUrl?: string;
  sourceId?: string;
  scrapedAt?: string;
  profile?: {
    name?: string;
    gender?: string;
    age?: number;
    nationality?: string;
    profileImageUrl?: string;
  };
  traits?: {
    occupation?: string;
    occupationCode?: string;
    smoking?: string;
    smokingCode?: string;
    personality?: string;
    personalityCode?: string;
    cleanliness?: string;
    sleepStyle?: string;
    sleepStyleCode?: string;
    lifestyleTags?: string[];
  };
  contact?: {
    phones?: string[];
    hasWhatsapp?: boolean;
  };
  location?: {
    citySlug?: string;
    cityId?: string;
    district?: string;
  };
  description?: string;
};

interface Props {
  importSource?: string | null;
  importSourceId?: string | null;
  importSourceUrl?: string | null;
  importedAt?: string | null;
  importedByAdminId?: number | null;
  /** Raw JSON string from backend; we parse here. Never renders if invalid. */
  sourceMetadata?: string | null;
}

function parseMeta(raw?: string | null): SourceMetadata | null {
  if (!raw) return null;
  try { return JSON.parse(raw) as SourceMetadata; } catch { return null; }
}

export default function ImportedSourceCard({
  importSource, importSourceId, importSourceUrl, importedAt, importedByAdminId, sourceMetadata,
}: Props) {
  const { t } = useTranslation();
  if (!importSource) return null;
  const meta = parseMeta(sourceMetadata);
  const profile = meta?.profile ?? {};
  const traits = meta?.traits ?? {};
  const contact = meta?.contact ?? {};
  const location = meta?.location ?? {};

  return (
    <Card
      style={{ marginTop: 16 }}
      title={
        <Space>
          <Tag color="geekblue">{importSource}</Tag>
          <Typography.Text>{t('importedSource.title')}</Typography.Text>
        </Space>
      }
      extra={
        importSourceUrl ? (
          <a href={importSourceUrl} target="_blank" rel="noopener noreferrer">
            <LinkOutlined /> {t('importedSource.openSource')}
          </a>
        ) : null
      }
    >
      {!meta ? (
        <Empty description={t('importedSource.noMetadata')} />
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: '160px 1fr', gap: 24, alignItems: 'flex-start' }}>
          {/* Left: profile photo or avatar placeholder */}
          <div style={{ textAlign: 'center' }}>
            {profile.profileImageUrl ? (
              <Image
                src={profile.profileImageUrl}
                width={140}
                height={140}
                style={{ objectFit: 'cover', borderRadius: '50%', border: '2px solid #f0f0f0' }}
                preview
                alt={profile.name || ''}
              />
            ) : (
              <div style={{
                width: 140, height: 140, borderRadius: '50%',
                background: '#f5f5f5', display: 'flex', alignItems: 'center',
                justifyContent: 'center', margin: '0 auto', border: '2px solid #f0f0f0',
              }}>
                <UserOutlined style={{ fontSize: 48, color: '#bfbfbf' }} />
              </div>
            )}
            <div style={{ marginTop: 8 }}>
              <Typography.Text strong>{profile.name || '—'}</Typography.Text>
            </div>
            {(profile.gender || profile.age || profile.nationality) && (
              <Typography.Text type="secondary" style={{ display: 'block', fontSize: 12 }}>
                {[
                  profile.gender,
                  profile.age ? t('importedSource.ageYears', { age: profile.age }) : null,
                  profile.nationality,
                ].filter(Boolean).join(' · ')}
              </Typography.Text>
            )}
          </div>

          {/* Right: structured details */}
          <div>
            <Descriptions size="small" column={2} bordered>
              <Descriptions.Item label={t('importedSource.gender')}>
                {profile.gender
                  ? <Tag icon={profile.gender.includes('أنث') ? <WomanOutlined /> : <ManOutlined />}>{profile.gender}</Tag>
                  : '—'}
              </Descriptions.Item>
              <Descriptions.Item label={t('importedSource.age')}>
                {profile.age ? t('importedSource.ageYears', { age: profile.age }) : '—'}
              </Descriptions.Item>
              <Descriptions.Item label={t('importedSource.nationality')}>{profile.nationality || '—'}</Descriptions.Item>
              <Descriptions.Item label={t('importedSource.occupation')}>
                {traits.occupation ? <Tag color="blue" icon={<CoffeeOutlined />}>{traits.occupation}</Tag> : '—'}
              </Descriptions.Item>
              <Descriptions.Item label={t('importedSource.smoking')}>
                {traits.smoking
                  ? <Tag color={traits.smokingCode === 'NON_SMOKER' ? 'green' : 'volcano'}>{traits.smoking}</Tag>
                  : '—'}
              </Descriptions.Item>
              <Descriptions.Item label={t('importedSource.personality')}>
                {traits.personality ? <Tag color="purple">{traits.personality}</Tag> : '—'}
              </Descriptions.Item>
              <Descriptions.Item label={t('importedSource.cleanliness')}>
                {traits.cleanliness ? <Tag color="cyan">{traits.cleanliness}</Tag> : '—'}
              </Descriptions.Item>
              <Descriptions.Item label={t('importedSource.sleepStyle')}>
                {traits.sleepStyle ? <Tag color="gold">{traits.sleepStyle}</Tag> : '—'}
              </Descriptions.Item>
              <Descriptions.Item label={t('importedSource.city')}>
                <Space>
                  <HomeOutlined />
                  {location.citySlug || '—'}
                  {location.cityId ? <Typography.Text type="secondary">#{location.cityId}</Typography.Text> : null}
                </Space>
              </Descriptions.Item>
              <Descriptions.Item label={t('importedSource.district')}>
                {location.district || '—'}
              </Descriptions.Item>
              <Descriptions.Item label={t('importedSource.phones')} span={2}>
                {contact.phones && contact.phones.length > 0
                  ? <Space wrap>
                      {contact.phones.map((p, i) => <Tag key={i}>{p}</Tag>)}
                      {contact.hasWhatsapp && (
                        <Tag color="green" icon={<WhatsAppOutlined />}>{t('importedSource.whatsappReachable')}</Tag>
                      )}
                    </Space>
                  : '—'}
              </Descriptions.Item>
              {traits.lifestyleTags && traits.lifestyleTags.length > 0 && (
                <Descriptions.Item label={t('importedSource.allTags')} span={2}>
                  <Space wrap>
                    {traits.lifestyleTags.map((tg, i) => <Tag key={i}>{tg}</Tag>)}
                  </Space>
                </Descriptions.Item>
              )}
              {meta.description && (
                <Descriptions.Item label={t('importedSource.description')} span={2}>
                  <Typography.Paragraph style={{ margin: 0, whiteSpace: 'pre-wrap' }}>
                    {meta.description}
                  </Typography.Paragraph>
                </Descriptions.Item>
              )}
              <Descriptions.Item label={t('importedSource.sourceId')}>{importSourceId || '—'}</Descriptions.Item>
              <Descriptions.Item label={t('importedSource.importedAt')}>
                {importedAt ? new Date(importedAt).toLocaleString() : '—'}
              </Descriptions.Item>
              {importedByAdminId != null && (
                <Descriptions.Item label={t('importedSource.importedByAdminId')} span={2}>
                  #{importedByAdminId}
                </Descriptions.Item>
              )}
            </Descriptions>
          </div>
        </div>
      )}
    </Card>
  );
}
